$type
<script setup lang="ts">
import { api } from '@/api';
import {
  EntityWithCommentType,
  instanceOfLinearAcceleratorDto,
  instanceOfOtherMvTherapyMachineDto,
  ReferencingObjectDto,
  type MedicalSystemRtSystemDto,
} from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { TabDefinition, TabIndicatorState } from '@/components/Tabs/types';
import { routenames } from '@/router/routenames';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useDeleteMedicalSystemWithConfirmation } from '../utils';
import MedicalSystemFormAdministrativeTab from './MedicalSystemFormAdministrativeTab.vue';
import MedicalSystemFormConfigurationTab from './MedicalSystemFormConfigurationTab.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { useFormTab } from '@/components/forms/useFormTab';

const { t } = useTranslation();
const values = useFormValues<MedicalSystemRtSystemDto>();
const router = useRouter();
const mode = useFormMode();
const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        indicatorState: getIndicatorState(['name', 'attachments'])
          ? TabIndicatorState.ERROR
          : undefined,
      },
      configuration: {
        ...defaultPageTabs.configuration,
        hidden:
          !instanceOfLinearAcceleratorDto(values.value) &&
          !instanceOfOtherMvTherapyMachineDto(values.value),
        indicatorState: getIndicatorState(['sourceAxisDistance']),
      },
      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },

      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || mode.value === 'add',
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);

const { action: deleteAction } = useDeleteMedicalSystemWithConfirmation();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.medicalSystems.edit,
        params: { id: values.value.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'MedicalSystem_one',
              },
              t,
            ),
          });
          if (result?.success) {
            router.push({ name: routenames.medicalSystems.overview });
          }
        }
      },
    },
  ];
});
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <InlineNotification
        v-if="values.isReferenced && mode === 'edit'"
        variant="flat"
        class="mb-4"
        :value="{
          type: 'info',
          message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
            entity: $t('MedicalSystem_one'),
          }),
        }"
      >
        <template #append>
          <span
            class="ml-2 linkButton"
            @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
            >{{ $t('Info') }}</span
          >
        </template>
      </InlineNotification>
      <MedicalSystemFormAdministrativeTab />
    </template>

    <template #configuration>
      <InlineNotification
        v-if="values.isReferenced && mode === 'edit'"
        variant="flat"
        class="mb-4"
        :value="{
          type: 'info',
          message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
            entity: $t('MedicalSystem_one'),
          }),
        }"
      >
        <template #append>
          <span
            class="ml-2 linkButton"
            @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
            >{{ $t('Info') }}</span
          >
        </template>
      </InlineNotification>
      <MedicalSystemFormConfigurationTab />
    </template>
    <template #comments>
      <Comments
        v-if="values.id"
        :id="values.id"
        :entityWithCommentType="EntityWithCommentType.MedicalSystem"
        @change="commentsChanged"
      />
    </template>
    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="api.medicalSystemsApi.getMedicalSystemHistory.bind(api.medicalSystemsApi)"
      />
    </template>
  </BasePageNavigation>
  <EntityReferencesModal
    v-if="values.isReferenced"
    :references="entityReferences"
    :title="$t('ReferenceingEntities')"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen) entityReferences = null;
      }
    "
  ></EntityReferencesModal>
</template>
